import { AlertCircle } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AssetsInputWithTags } from './AssetsInputWithTags'
import { INDUSTRIES_OF_INTEREST, INVESTMENT_AMOUNT_OPTIONS } from './ProfilePreferencesScreenBase'
import type { ProfilePreferencesScreenBaseProps } from './ProfilePreferencesScreenBase'
import logoSecondLane from '@/assets/logoSecondLane.svg'
import logoSecondLaneDark from '@/assets/logoSecondLaneDark.svg'

const fieldLabelClass = 'font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-white'
const inputClass = '!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] dark:!border-[rgba(255,255,255,0.12)] !bg-[rgba(255,255,255,0.52)] dark:!bg-[#1c1c1c] !px-3 !py-[14px] text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] placeholder:!text-[rgba(45,41,35,0.72)] dark:placeholder:!text-[rgba(255,255,255,0.72)] focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:focus:!border-[rgba(255,255,255,0.12)] hover:!border-[rgba(45,41,35,0.12)] dark:hover:!border-[rgba(255,255,255,0.12)]'

export const ProfilePreferencesScreenMobile = ({
  formData,
  loading,
  error,
  onFormDataChange,
  onIndustryToggle,
  onSelectAllIndustries,
  onClearAllIndustries,
  onSubmit,
  onSkip,
}: ProfilePreferencesScreenBaseProps) => {
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    onFormDataChange({ [field]: value })
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0d0d0d] px-6 pb-8 pt-6">
      <div className="mx-auto max-w-[402px]">
        <div className="mb-12 flex justify-center">
          <img src={logoSecondLane} alt="SecondLane" className="h-6 w-24 dark:hidden" />
          <img src={logoSecondLaneDark} alt="SecondLane" className="hidden h-6 w-24 dark:block" />
        </div>

        <div className="mb-12 text-center">
          <h1 className="font-plus-jakarta text-[30px] font-medium leading-[40px] tracking-[-0.28px] text-[#2d2923] dark:text-white">
            Account Verification Required
          </h1>
          <p className="font-plus-jakarta text-[16px] font-normal leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(255,255,255,0.64)]">
            Please complete the following attestation to access your account
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 border border-red-500 bg-red-500/10 px-4 py-3 text-red-500">
              <AlertCircle size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-6">
            <h2 className="font-plus-jakarta text-[20px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-white">
              Profile Information
            </h2>

            <div className="space-y-3">
              <label className={fieldLabelClass}>First name</label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="Enter your first name"
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-3">
              <label className={fieldLabelClass}>Last Name</label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Enter your last name"
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-3">
              <label className={fieldLabelClass}>Job Title</label>
              <Input
                type="text"
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
                placeholder="e.g., Investment Manager, CEO"
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-3">
              <label className={fieldLabelClass}>Company Name</label>
              <Input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Enter your company name"
                className={inputClass}
                required
              />
            </div>

            <div className="space-y-3">
              <label className={fieldLabelClass}>Country</label>
              <Select value={formData.country} onValueChange={(value) => handleInputChange('country', value)}>
                <SelectTrigger className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] dark:!border-[rgba(255,255,255,0.12)] !bg-white dark:!bg-[#0d0d0d] !px-3 !py-[14px] font-plus-jakarta text-[14px] font-medium leading-[20px] !text-[#2d2923] dark:!text-white hover:!bg-white dark:hover:!bg-[#0d0d0d] focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:focus:!border-[rgba(255,255,255,0.12)]">
                  <SelectValue placeholder="United States" />
                </SelectTrigger>
                <SelectContent className="!bg-white dark:!bg-[#1c1c1c] text-[#2d2923] dark:text-white border-[rgba(45,41,35,0.12)] dark:border-[rgba(255,255,255,0.12)]">
                  <SelectItem value="United States">United States</SelectItem>
                  <SelectItem value="United Arab Emirates">UAE</SelectItem>
                  <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                  <SelectItem value="Australia">Australia</SelectItem>
                  <SelectItem value="Brazil">Brazil</SelectItem>
                  <SelectItem value="Canada">Canada</SelectItem>
                  <SelectItem value="France">France</SelectItem>
                  <SelectItem value="Germany">Germany</SelectItem>
                  <SelectItem value="Hong Kong">Hong Kong</SelectItem>
                  <SelectItem value="India">India</SelectItem>
                  <SelectItem value="Japan">Japan</SelectItem>
                  <SelectItem value="Singapore">Singapore</SelectItem>
                  <SelectItem value="South Korea">South Korea</SelectItem>
                  <SelectItem value="Switzerland">Switzerland</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <AssetsInputWithTags
              label="Share the assets you're interested in buying to receive exclusive alerts"
              value={formData.companies_of_interest_buy}
              onChange={(assets) => onFormDataChange({ companies_of_interest_buy: assets })}
              placeholder="e.g., Anduril, SpaceX, OpenAI (separate with commas, semicolons, or new lines)"
            />

            <AssetsInputWithTags
              label="Let us know which assets you're planning to sell for expert advice."
              value={formData.companies_of_interest_sell}
              onChange={(assets) => onFormDataChange({ companies_of_interest_sell: assets })}
              placeholder="e.g., Anduril, SpaceX, Palantir (separate with commas, semicolons, or new lines)"
            />

            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-white">What categories excite you?</p>
                  <p className="font-plus-jakarta text-[12px] font-normal leading-[16px] text-[#2d2923] dark:text-white">Let us know to unlock relevant resources!</p>
                </div>
                <div className="flex items-center gap-3 pt-px">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      onSelectAllIndustries()
                    }}
                    className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[#2d2923] dark:text-white underline"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      onClearAllIndustries()
                    }}
                    className="font-plus-jakarta text-[13px] font-medium leading-[18px] text-[#2d2923] dark:text-white underline"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              <div className="border border-[rgba(45,41,35,0.12)] dark:border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] p-[11px]">
                <div className="flex flex-wrap gap-2">
                  {INDUSTRIES_OF_INTEREST.map((industry) => {
                    const selected = formData.industries_of_interest.includes(industry)
                    return (
                      <button
                        key={industry}
                        type="button"
                        onClick={() => onIndustryToggle(industry)}
                        className={`px-3 py-2 font-plus-jakarta text-[13px] font-medium leading-[18px] border transition-colors ${
                          selected
                          ? 'bg-[#2d2923] text-white border-[rgba(255,255,255,0.82)] dark:bg-white dark:text-[#2d2923] dark:border-[rgba(45,41,35,0.82)]'
                          : 'bg-[rgba(255,255,255,0.72)] text-[#2d2923] border-[rgba(45,41,35,0.12)] dark:bg-[rgba(28,28,28,0.32)] dark:text-white dark:border-[rgba(255,255,255,0.12)]'
                        }`}
                      >
                        {industry}
                      </button>
                    )
                  })}
                </div>
              </div>

              <p className="font-plus-jakarta text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]">
                Select: {formData.industries_of_interest.length} of {INDUSTRIES_OF_INTEREST.length}
              </p>
            </div>

            <h2 className="font-plus-jakarta text-[20px] font-medium leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-white">
              Investment Amount Preferences
            </h2>

            <div className="space-y-3">
              <label className={fieldLabelClass}>What is your minimum investment amount?</label>
              <Select
                value={formData.preferred_investment_amount_min?.toString() || ''}
                onValueChange={(value) => onFormDataChange({ preferred_investment_amount_min: value ? parseInt(value, 10) : undefined })}
              >
                <SelectTrigger className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] dark:!border-[rgba(255,255,255,0.12)] !bg-white dark:!bg-[rgba(28,28,28,0.52)] !px-3 !py-[14px] font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] hover:!bg-white dark:hover:!bg-[rgba(28,28,28,0.52)] focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:focus:!border-[rgba(255,255,255,0.12)]">
                  <SelectValue placeholder="$100,000" />
                </SelectTrigger>
                <SelectContent className="!bg-white dark:!bg-[#1c1c1c] text-[#2d2923] dark:text-white border-[rgba(45,41,35,0.12)] dark:border-[rgba(255,255,255,0.12)]">
                  {INVESTMENT_AMOUNT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <label className={fieldLabelClass}>What is your maximum investment amount?</label>
              <Select
                value={formData.preferred_investment_amount_max?.toString() || ''}
                onValueChange={(value) => onFormDataChange({ preferred_investment_amount_max: value ? parseInt(value, 10) : undefined })}
              >
                <SelectTrigger className="!h-12 !rounded-none !border-[rgba(45,41,35,0.12)] dark:!border-[rgba(255,255,255,0.12)] !bg-white dark:!bg-[rgba(28,28,28,0.52)] !px-3 !py-[14px] font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[rgba(255,255,255,0.72)] hover:!bg-white dark:hover:!bg-[rgba(28,28,28,0.52)] focus:!ring-0 focus:!border-[rgba(45,41,35,0.12)] dark:focus:!border-[rgba(255,255,255,0.12)]">
                  <SelectValue placeholder="$200,000" />
                </SelectTrigger>
                <SelectContent className="!bg-white dark:!bg-[#1c1c1c] text-[#2d2923] dark:text-white border-[rgba(45,41,35,0.12)] dark:border-[rgba(255,255,255,0.12)]">
                  {INVESTMENT_AMOUNT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={onSkip}
                className="h-12 border border-[#2d2923] dark:border-white bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(45,41,35,0.72)] px-4 font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-white"
              >
                SKIP FOR NOW
              </button>

              <Button
                type="submit"
                variant="primary"
                className="!h-12 !rounded-none !bg-[#2d2923] dark:!bg-[#ecebeb] !px-4 font-plus-jakarta text-[14px] font-medium leading-[20px] !text-white dark:!text-[#2d2923] hover:!bg-[#2d2923] dark:hover:!bg-[#ecebeb] hover:!text-white dark:hover:!text-[#2d2923]"
                isLoading={loading}
              >
                SAVE PREFERENCES
              </Button>
            </div>

            <p className="text-center font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]">
              You can always update these preferences later in your profile settings.
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
